import type { SupabaseClient } from '@supabase/supabase-js';
import { logError } from './errorHandler';

const HANDOFF_PARAM = 'handoff';

// The accepted argument is auth-js's own, not a hand copy of it: a renamed or
// re-cased key upstream has to break `tsc -b` here instead of being silently
// accepted by a locally invented interface.
type SetSession = SupabaseClient['auth']['setSession'];
type HandoffSession = Parameters<SetSession>[0];

interface HandoffClient {
  auth: {
    setSession: SetSession;
  };
}

function stripHandoffParam() {
  const url = new URL(window.location.href);
  url.searchParams.delete(HANDOFF_PARAM);
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

/**
 * Completes a session handoff started on another registrable domain, where a
 * shared cookie can never reach. A rejected or spent code is not an error state:
 * the visitor simply lands on the normal login flow.
 */
export async function consumeHandoffSession({
  supabase,
  baseUrl,
}: {
  supabase: HandoffClient;
  baseUrl: string;
}): Promise<boolean> {
  const code = new URLSearchParams(window.location.search).get(HANDOFF_PARAM);
  if (!code) return false;

  // Spend the URL immediately; the code is single-use, so leaving it in the
  // address bar and browser history only creates a copy that cannot work.
  stripHandoffParam();

  try {
    const response = await fetch(
      `${baseUrl}/functions/v1/auth-bridge/handoff/exchange`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      },
    );
    if (!response.ok) return false;

    const payload = (await response.json()) as { session?: Partial<HandoffSession> | null };
    const accessToken = payload?.session?.access_token;
    const refreshToken = payload?.session?.refresh_token;
    // A handoff without a refresh token would sign the visitor in for one hour and
    // then drop them, which is worse than not handing off at all, so both keys are
    // required before anything is written into the client.
    if (typeof accessToken !== 'string' || typeof refreshToken !== 'string') return false;

    const { error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    return !error;
  } catch (error) {
    logError(error, 'Consume OpenPenPal session handoff');
    return false;
  }
}
